// pages/public/AmbulanceRequestForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Ambulance, MapPin, User, Phone, AlertCircle, Clock,
  ChevronLeft, CheckCircle, Hospital, Info
} from 'lucide-react';
import ambulanceService from '../../services/ambulanceService';
import { hospitalService } from '../../services/hospitalService';

const reverseGeocode = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
      { headers: { 'User-Agent': 'MediGo-App' } }
    );
    const data = await response.json();
    return data.display_name || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }
};

const AmbulanceRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ambulance, pickupLocation: initialPickup, nearestHospitals } = location.state || {};

  const [formData, setFormData] = useState({
    requestType: 'emergency',
    priority: 'high',
    patientName: '',
    patientAge: '',
    patientGender: '',
    patientPhone: '',
    condition: '',
    symptoms: '',
    isConscious: true,
    canWalk: false,
    pickupAddress: '',
    pickupLandmark: '',
    pickupInstructions: '',
    dropoffAddress: '',
    dropoffFacility: '',
    dropoffContact: '',
    dropoffPhone: '',
    oxygenRequired: false,
    wheelchairAccess: false,
    stretcherRequired: false,
    medicalEscort: false,
    specialNotes: ''
  });

  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [hospitals, setHospitals] = useState(nearestHospitals || []);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [estimatedCost, setEstimatedCost] = useState(null);

  useEffect(() => {
    if (!ambulance) {
      navigate('/emergency');
      return;
    }

    if (initialPickup) {
      setPickupCoords([initialPickup.longitude, initialPickup.latitude]);
      setFormData(prev => ({
        ...prev,
        pickupAddress: initialPickup.address || ''
      }));
    } else {
      getCurrentLocation();
    }

    if (!nearestHospitals || nearestHospitals.length === 0) {
      fetchNearbyHospitals();
    }
  }, []);

  useEffect(() => {
    if (pickupCoords && dropoffCoords && ambulance) {
      calculateEstimatedCost();
    }
  }, [pickupCoords, dropoffCoords]);

  const getCurrentLocation = async () => {
    setLoadingLocation(true);
    const result = await ambulanceService.getCurrentLocation();
    
    if (result.success) {
      const { latitude, longitude } = result.data;
      setPickupCoords([longitude, latitude]);
      
      const address = await reverseGeocode(latitude, longitude);
      setFormData(prev => ({
        ...prev,
        pickupAddress: address
      }));
    } else {
      setError(result.message || 'Failed to get your location. Please enter it manually.');
    }
    
    setLoadingLocation(false);
  };

  const fetchNearbyHospitals = async () => {
    const result = await ambulanceService.getCurrentLocation();
    
    if (result.success) {
      try {
        setLoadingHospitals(true);
        const response = await hospitalService.getNearbyHospitals({
          latitude: result.data.latitude,
          longitude: result.data.longitude,
          radius: 50,
          limit: 10,
          emergency: true
        });

        if (response.success) {
          setHospitals(response.data.hospitals || []);
        }
      } catch (err) {
        console.error('Fetch hospitals error:', err);
      } finally {
        setLoadingHospitals(false);
      }
    }
  };

  const calculateEstimatedCost = () => {
    if (!pickupCoords || !dropoffCoords || !ambulance) return;

    const distance = calculateDistance(
      pickupCoords[1], pickupCoords[0],
      dropoffCoords[1], dropoffCoords[0]
    );

    const baseFee = ambulance.pricing?.baseFee || 0;
    const perKmRate = ambulance.pricing?.perKmRate || 0;
    const distanceFee = distance * perKmRate;
    const total = baseFee + distanceFee;

    setEstimatedCost({
      distance: distance.toFixed(1),
      baseFee,
      distanceFee: distanceFee.toFixed(2),
      total: total.toFixed(2)
    });
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const toRad = (degrees) => degrees * (Math.PI / 180);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleHospitalSelect = async (hospital) => {
    setFormData(prev => ({
      ...prev,
      dropoffAddress: hospital.location?.address || '',
      dropoffFacility: hospital.name,
      dropoffContact: hospital.name,
      dropoffPhone: hospital.contact?.emergency || hospital.contact?.phone || ''
    }));

    if (hospital.location?.coordinates) {
      setDropoffCoords(hospital.location.coordinates);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.patientName || !formData.condition) {
      setError('Patient name and condition are required');
      return;
    }

    if (!pickupCoords) {
      setError('Pickup location is required. Please allow location access or enter it manually.');
      return;
    }

    if (!formData.pickupAddress) {
      setError('Pickup address is required');
      return;
    }

    if (!formData.dropoffAddress || !dropoffCoords) {
      setError('Dropoff location is required');
      return;
    }

    try {
      setIsSubmitting(true);

      const requestData = {
        ambulanceId: ambulance._id,
        requestType: formData.requestType,
        priority: formData.priority,
        patientInfo: {
          name: formData.patientName,
          age: parseInt(formData.patientAge) || undefined,
          gender: formData.patientGender,
          phone: formData.patientPhone,
          condition: formData.condition,
          symptoms: formData.symptoms,
          isConscious: formData.isConscious,
          canWalk: formData.canWalk
        },
        pickupLocation: {
          coordinates: pickupCoords,
          address: formData.pickupAddress,
          landmark: formData.pickupLandmark,
          instructions: formData.pickupInstructions
        },
        dropoffLocation: {
          coordinates: dropoffCoords,
          address: formData.dropoffAddress,
          facilityName: formData.dropoffFacility,
          contactPerson: formData.dropoffContact,
          contactPhone: formData.dropoffPhone
        },
        specialRequirements: {
          oxygenRequired: formData.oxygenRequired,
          wheelchairAccess: formData.wheelchairAccess,
          stretcherRequired: formData.stretcherRequired,
          medicalEscort: formData.medicalEscort,
          notes: formData.specialNotes
        }
      };

      const response = await ambulanceService.requestAmbulance(requestData);

      if (response.success) {
        navigate('/ambulances/my-requests', {
          state: {
            success: true,
            message: 'Ambulance requested successfully!',
            requestId: response.data._id
          }
        });
      } else {
        setError(response.message || 'Failed to request ambulance');
      }
    } catch (err) {
      console.error('Submit request error:', err);
      setError('Failed to submit request. Please try calling the ambulance directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ambulance) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Ambulance</h1>
          <p className="text-gray-600">Complete the form to request an ambulance service</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <Ambulance className="w-8 h-8 text-red-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">{ambulance.name}</h2>
              <p className="text-gray-600 text-sm mb-2">{ambulance.description}</p>
              <div className="flex flex-wrap gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>{ambulance.contact?.phone}</span>
                </div>
                {ambulance.rating?.average > 0 && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{ambulance.rating.average.toFixed(1)} rating</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-start gap-3 mb-6">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 font-bold text-xl">
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Request Type</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                <select
                  name="requestType"
                  value={formData.requestType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="emergency">Emergency</option>
                  <option value="non-emergency">Non-Emergency</option>
                  <option value="patient_transfer">Patient Transfer</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority *</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Patient Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Patient Name *</label>
                <input
                  type="text"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                <input
                  type="number"
                  name="patientAge"
                  value={formData.patientAge}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Age"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  name="patientGender"
                  value={formData.patientGender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                <input
                  type="tel"
                  name="patientPhone"
                  value={formData.patientPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="+254 700 000 000"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Medical Condition / Emergency Type *</label>
                <input
                  type="text"
                  name="condition"
                  value={formData.condition}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="e.g., Heart attack, Accident injury, Breathing difficulty"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Symptoms / Additional Details</label>
                <textarea
                  name="symptoms"
                  value={formData.symptoms}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Describe symptoms and any relevant medical information"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isConscious"
                    checked={formData.isConscious}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Patient is conscious</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="canWalk"
                    checked={formData.canWalk}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Patient can walk</span>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Pickup Location
            </h3>
            
            {loadingLocation && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-blue-800">Getting your location...</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                <input
                  type="text"
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Enter pickup address"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Landmark</label>
                <input
                  type="text"
                  name="pickupLandmark"
                  value={formData.pickupLandmark}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Nearby landmark to help locate you"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Instructions</label>
                <textarea
                  name="pickupInstructions"
                  value={formData.pickupInstructions}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Any special instructions for finding the location"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Hospital className="w-5 h-5" />
              Dropoff Location
            </h3>

            {hospitals.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Select Hospital:</p>
                <div className="space-y-2">
                  {hospitals.slice(0, 3).map((hospital) => (
                    <button
                      key={hospital._id}
                      type="button"
                      onClick={() => handleHospitalSelect(hospital)}
                      className="w-full text-left p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      <p className="font-semibold text-sm">{hospital.name}</p>
                      <p className="text-xs text-gray-600">{hospital.location?.address}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-center">
                  <span className="text-sm text-gray-600">or enter manually below</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hospital/Facility Name</label>
                <input
                  type="text"
                  name="dropoffFacility"
                  value={formData.dropoffFacility}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Hospital or medical facility name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Destination Address *</label>
                <input
                  type="text"
                  name="dropoffAddress"
                  value={formData.dropoffAddress}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Enter destination address"
                  required
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                  <input
                    type="text"
                    name="dropoffContact"
                    value={formData.dropoffContact}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Contact person at destination"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    name="dropoffPhone"
                    value={formData.dropoffPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="+254 700 000 000"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Special Requirements</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="oxygenRequired"
                  checked={formData.oxygenRequired}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Oxygen Required</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="wheelchairAccess"
                  checked={formData.wheelchairAccess}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Wheelchair Access</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="stretcherRequired"
                  checked={formData.stretcherRequired}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Stretcher Required</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="medicalEscort"
                  checked={formData.medicalEscort}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Medical Escort Needed</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
              <textarea
                name="specialNotes"
                value={formData.specialNotes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Any other special requirements or information"
              />
            </div>
          </div>

          {estimatedCost && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Estimated Cost
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Distance:</span>
                  <span className="font-semibold">{estimatedCost.distance} km</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Base Fee:</span>
                  <span className="font-semibold">KES {estimatedCost.baseFee}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Distance Fee:</span>
                  <span className="font-semibold">KES {estimatedCost.distanceFee}</span>
                </div>
                <div className="border-t border-blue-300 pt-2 mt-2 flex justify-between">
                  <span className="font-bold text-gray-900">Estimated Total:</span>
                  <span className="font-bold text-gray-900 text-lg">KES {estimatedCost.total}</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  * This is an estimate. Final cost may vary based on actual distance and additional services.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Clock className="w-5 h-5 animate-spin" />
                  Submitting Request...
                </>
              ) : (
                <>
                  <Ambulance className="w-5 h-5" />
                  Request Ambulance
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AmbulanceRequestForm;